import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { sendInvitation, getRelationshipTypes } from '../../services/api';

function InviteFamily() {
  const navigate = useNavigate();
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRelation, setInviteRelation] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [invitationError, setInvitationError] = useState('');
  const [invitationSuccess, setInvitationSuccess] = useState('');
  const [relationshipTypes, setRelationshipTypes] = useState([]);
  const [groupedRelationships, setGroupedRelationships] = useState({});
  const [isLoadingRelationships, setIsLoadingRelationships] = useState(true);

  const handleSendInvitation = async () => {
    if (!inviteEmail || !inviteRelation) {
      setInvitationError('Please fill in both email and relationship');
      return;
    }

    setIsLoading(true);
    setInvitationError('');
    setInvitationSuccess('');

    try {
      // Get family data from localStorage
      const familyData = JSON.parse(sessionStorage.getItem('familySetupData') || '{}');
      
      // The inviteRelation now contains the ID directly from the dropdown
      const relationshipId = parseInt(inviteRelation);
      
      if (!relationshipId || isNaN(relationshipId)) {
        setInvitationError(`Invalid relationship selected. Please try again.`);
        return;
      }
      
      // The relationshipId is the ID from the dropdown, no lookup needed

      // Get the family ID from localStorage
      const familyId = localStorage.getItem('familyId');
      
      if (!familyId) {
        setInvitationError('Family ID not found. Please try creating your family again.');
        return;
      }

      // Prepare the API payload
      const invitationPayload = {
        family: familyId,
        invited_email: inviteEmail,
        relationship_type: relationshipId, // Send the numeric ID directly
        invitation_type: 'personal'
      };
      
      // Call the API
      const response = await sendInvitation(invitationPayload);
      
      // Show success message
      setInvitationSuccess(`Invitation sent successfully to ${inviteEmail}!`);

      // Clear the form
      setInviteEmail('');
      setInviteRelation('');

      // Navigate to My Life page to start chapters onboarding
      setTimeout(() => {
        navigate('/my-life');
      }, 1500);
      
    } catch (error) {
      console.error('Invitation error:', error);
      setInvitationError(error.message || 'Failed to send invitation');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch relationship types on component mount — same full set as Add Family Member
  useEffect(() => {
    const CATEGORY_ORDER = {
      'Immediate Family': ['Mother', 'Father', 'Son', 'Daughter', 'Brother', 'Sister'],
      'Spouse': ['Husband', 'Wife', 'Spouse'],
      'Grandparent': ['Grandmother', 'Grandfather', 'Grandson', 'Granddaughter'],
      'Aunt / Uncle': ['Aunt', 'Uncle', 'Nephew', 'Niece', 'Cousin'],
      'In-Laws': ['Mother-in-law', 'Father-in-law', 'Sister-in-law', 'Brother-in-law', 'Daughter-in-law', 'Son-in-law'],
      'Step-Family': ['Stepmother', 'Stepfather', 'Stepson', 'Stepdaughter', 'Stepbrother', 'Stepsister'],
    };

    const fetchRelationshipTypes = async () => {
      try {
        const data = await getRelationshipTypes();
        let allTypes = Array.isArray(data) ? data : (data.relationship_types || []);

        // Build a name→type map (case-insensitive, prefer capitalized)
        const nameMap = {};
        for (const t of allTypes) {
          const key = t.name.toLowerCase().replace(/-/g, '').replace(/ /g, '');
          if (!nameMap[key] || t.name[0] === t.name[0].toUpperCase()) {
            nameMap[key] = t;
          }
        }

        // Build grouped structure matching the category order
        const grouped = {};
        const used = new Set();
        for (const [category, names] of Object.entries(CATEGORY_ORDER)) {
          const items = [];
          for (const name of names) {
            const key = name.toLowerCase().replace(/-/g, '').replace(/ /g, '');
            const match = nameMap[key];
            if (match && !used.has(match.id)) {
              items.push(match);
              used.add(match.id);
            }
          }
          if (items.length > 0) grouped[category] = items;
        }

        setGroupedRelationships(grouped);
        // Flat list for fallback
        setRelationshipTypes(Object.values(grouped).flat());
      } catch (error) {
        console.error('Error fetching relationship types:', error);
        const fallback = [
          { id: 32, name: 'Mother' }, { id: 39, name: 'Father' },
          { id: 38, name: 'Son' }, { id: 43, name: 'Daughter' },
          { id: 44, name: 'Brother' }, { id: 37, name: 'Sister' },
          { id: 30, name: 'Husband' }, { id: 47, name: 'Wife' },
        ];
        setRelationshipTypes(fallback);
        setGroupedRelationships({ 'Family': fallback });
      } finally {
        setIsLoadingRelationships(false);
      }
    };

    fetchRelationshipTypes();
  }, []);

  return (
    <div className="min-h-screen bg-[#F5F5DC] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-300">
          {/* Header */}
          <div className="bg-[#556B2F] text-white p-6 text-center">
            <h1 className="text-2xl font-bold mb-2">Invite Your Family</h1>
            <p className="text-base opacity-90">Stories are better when shared</p>
          </div>
          
          {/* Content */}
          <div className="p-8">
            {/* Personal Invitations Section */}
            <div className="bg-gray-50 p-5 rounded-lg mb-6">
              <h3 className="text-lg font-bold text-[#556B2F] mb-3">Send Personal Invitations</h3>
              
              <div className="space-y-4">
                <div>
                  <label htmlFor="inviteEmail" className="block text-base font-bold text-gray-700 mb-2">
                    Family Member's Email
                  </label>
                  <input
                    type="email"
                    id="inviteEmail"
                    placeholder="mom@email.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    className="w-full px-4 py-4 text-lg border-2 border-gray-300 rounded-lg focus:border-[#556B2F] focus:outline-none"
                  />
                </div>
                
                <div>
                  <label htmlFor="inviteRelation" className="block text-base font-bold text-gray-700 mb-2">
                    Their Relationship to You
                  </label>
                  <select
                    id="inviteRelation"
                    value={inviteRelation}
                    onChange={(e) => {
                      setInviteRelation(e.target.value);
                    }}
                    className="w-full px-4 py-4 text-base border-2 border-gray-300 rounded-lg focus:border-[#556B2F] focus:outline-none bg-white"
                    disabled={isLoadingRelationships}
                  >
                    <option value="">
                      {isLoadingRelationships ? 'Loading relationships...' : 'Select relationship...'}
                    </option>
                    {Object.entries(groupedRelationships).map(([category, types]) => (
                      <optgroup key={category} label={category}>
                        {types.map(type => (
                          <option key={type.id} value={type.id}>
                            My {type.name}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>
                
                {invitationError && (
                  <p className="text-red-500 text-sm mt-2">{invitationError}</p>
                )}
                {invitationSuccess && (
                  <p className="text-green-600 text-sm mt-2">{invitationSuccess}</p>
                )}
                <button
                  onClick={handleSendInvitation}
                  disabled={!inviteEmail || !inviteRelation || isLoading}
                  className="w-full bg-[#6B8E23] text-white border-none py-4 px-4 text-base rounded-lg cursor-pointer transition-colors hover:bg-[#5A7A1F] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Sending...' : 'Send Invitation'}
                </button>
              </div>
            </div>

            <button
              onClick={() => navigate('/my-life')}
              className="w-full bg-gray-200 text-gray-700 border-none py-3 px-4 text-base rounded-lg cursor-pointer transition-colors hover:bg-gray-300"
            >
              Skip for now
            </button>

            <p className="text-sm text-center text-gray-600 mt-4">
              You can invite family members anytime from settings
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default InviteFamily; 